import api from './client';
import { adminApi } from './adminAuth';

export interface AdminMetrics {
  revenue: {
    mrr: number;
    arr: number;
    by_plan: {
      [plan: string]: {
        monthly: number;
        yearly: number;
        total_mrr: number;
      };
    };
    currency: string;
  };
  subscriptions: {
    total: number;
    by_plan: {
      [plan: string]: {
        total: number;
        active: number;
        trialing: number;
        expired: number;
        canceled: number;
      };
    };
    active_trials: number;
    converted: number;
    expired_trials: number;
    canceled: number;
  };
  organizations: {
    total: number;
    new_last_30_days: number;
  };
  users: {
    total: number;
    by_plan: {
      [plan: string]: {
        user_count: number;
        org_count: number;
      };
    };
  };
  resources: {
    projects: {
      total: number;
      by_plan: {
        [plan: string]: number;
      };
    };
    architectures: {
      total: number;
      by_plan: {
        [plan: string]: number;
      };
    };
  };
  health: {
    payment_failures: number;
    trial_conversion_rate: number;
  };
  recent_activity: Array<{
    date: string;
    plan: string;
    count: number;
  }>;
}

/**
 * Get admin dashboard metrics (requires super admin token)
 */
export async function getAdminMetrics(): Promise<{ success: boolean; metrics: AdminMetrics }> {
  const response = await adminApi.get('/api/admin/metrics');
  return response.data;
}

export interface AdminStats {
  organizations: number;
  users: number;
  active_subscriptions: number;
  mrr: number;
  executions: { total: number; last_7d: number };
}

/**
 * Get quick stats for admin dashboard
 */
export async function getAdminStats(): Promise<AdminStats> {
  const response = await adminApi.get('/api/admin/stats');
  return response.data;
}

export interface AdminUserRow {
  id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  is_super_admin: boolean;
  created_at: string;
  last_login: string | null;
  organization_id: string;
  organization_name: string;
  subscription_plan: string;
  subscription_status: string;
}

/**
 * List all users across all organizations
 */
export async function getAdminUsers(params?: {
  page?: number;
  limit?: number;
  search?: string;
}): Promise<{ users: AdminUserRow[]; total: number; page: number; limit: number }> {
  const response = await adminApi.get('/api/admin/users', { params });
  return response.data;
}

export interface AdminOrgRow {
  id: string;
  name: string;
  slug: string;
  created_at: string;
  plan: string;
  subscription_status: string;
  billing_cycle: string;
  trial_end: string | null;
  user_count: number;
  owner_email: string | null;
}

export async function getAdminOrganizations(params?: {
  page?: number;
  limit?: number;
  search?: string;
  plan?: string;
  status?: string;
}): Promise<{ organizations: AdminOrgRow[]; total: number; page: number; limit: number }> {
  const response = await adminApi.get('/api/admin/organizations', { params });
  return response.data;
}

export interface AdminSubscriptionRow {
  id: string;
  plan: string;
  status: string;
  billing_cycle: string;
  trial_end: string | null;
  current_period_end: string | null;
  created_at: string;
  updated_at: string;
  organization_id: string;
  organization_name: string;
  organization_slug: string;
  user_count: number;
}

export async function getAdminSubscriptions(params?: {
  page?: number;
  limit?: number;
  plan?: string;
  status?: string;
  search?: string;
}): Promise<{ subscriptions: AdminSubscriptionRow[]; total: number; page: number; limit: number }> {
  const response = await adminApi.get('/api/admin/subscriptions', { params });
  return response.data;
}

export interface AdminAuditLogRow {
  id: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  details: Record<string, unknown> | null;
  ip_address: string | null;
  created_at: string;
  admin_email: string;
  admin_name: string;
}

export async function getAdminAuditLogs(params?: {
  page?: number;
  limit?: number;
  search?: string;
  action?: string;
  date_from?: string;
  date_to?: string;
}): Promise<{ logs: AdminAuditLogRow[]; total: number; page: number; limit: number; actions: string[] }> {
  const response = await adminApi.get('/api/admin/audit-logs', { params });
  return response.data;
}

export async function exportAdminAuditLogsCsv(params?: {
  search?: string;
  action?: string;
  date_from?: string;
  date_to?: string;
}): Promise<Blob> {
  const response = await adminApi.get('/api/admin/audit-logs', {
    params: { ...params, export: 'csv' },
    responseType: 'blob',
  });
  return response.data;
}

// ─── ADO config (DB) ─────────────────────────────────────────────────────────

export interface AdoConfigRow {
  id: string;
  plan_code: string;
  organization: string;
  base_url: string;
  pipeline_id: number;
  project_name: string;
  has_pat: boolean;
  is_active: boolean;
  health_status: string;
  last_health_check: string | null;
  pat_expires_at: string | null;
  updated_at: string;
}

export async function testAdminAdoConfig(
  planCode: string
): Promise<{ success: boolean; status: string; message: string }> {
  const response = await adminApi.post(`/api/admin/ado-config/${planCode}/test`);
  return response.data;
}

export async function getAdminAdoConfig(): Promise<{ configs: AdoConfigRow[] }> {
  const response = await adminApi.get('/api/admin/ado-config');
  return response.data;
}

export async function updateAdminAdoConfig(
  planCode: string,
  data: Partial<AdoConfigRow> & { pat?: string }
): Promise<{ config: AdoConfigRow }> {
  const response = await adminApi.put(`/api/admin/ado-config/${planCode}`, data);
  return response.data;
}

// ─── SMTP config (DB) ────────────────────────────────────────────────────────

export interface AdminSmtpConfig {
  id: string;
  host: string;
  port: number;
  secure: boolean;
  user_email: string;
  from_email: string;
  from_name: string;
  has_pass: boolean;
  is_active: boolean;
  test_status: string | null;
  updated_at: string;
}

export async function getAdminSmtpConfig(): Promise<{ config: AdminSmtpConfig | null }> {
  const response = await adminApi.get('/api/admin/smtp-config');
  return response.data;
}

export async function updateAdminSmtpConfig(
  data: Partial<AdminSmtpConfig> & { password?: string }
): Promise<{ config: AdminSmtpConfig }> {
  const response = await adminApi.put('/api/admin/smtp-config', data);
  return response.data;
}

export async function testAdminSmtpConfig(
  to: string
): Promise<{ success: boolean; message: string }> {
  const response = await adminApi.post('/api/admin/smtp-config/test', { to });
  return response.data;
}

// ─── Plans ───────────────────────────────────────────────────────────────────

export interface AdminPlanConfig {
  display_name: string;
  price_monthly_cents: number | null;
  price_yearly_cents: number | null;
  display_price: boolean;
  trial_days: number;
  projects: number;
  architectures: number;
  deployments_per_month: number;
  pipeline_minutes_per_month: number;
  users_per_organization: number;
  member_price_per_additional_cents: number | null;
  member_max_total: number | null;
  storage_gb: number;
  concurrent_builds: number;
  azure_devops_org: string;
  shared_organization: boolean;
  features: Record<string, boolean | string>;
  is_active: boolean;
  sort_order: number;
  updated_at?: string;
}

export async function getAdminPlans(): Promise<{ plans: Record<string, AdminPlanConfig>; source: string }> {
  const response = await adminApi.get('/api/admin/plans');
  return response.data;
}

export async function updateAdminPlan(
  planCode: string,
  data: Partial<AdminPlanConfig>
): Promise<{ plan: AdminPlanConfig }> {
  const response = await adminApi.put(`/api/admin/plans/${planCode}`, data);
  return response.data;
}

// ─── Promo codes ─────────────────────────────────────────────────────────────

export interface PromoCode {
  id: string;
  code: string;
  description: string | null;
  discount_type: 'percentage' | 'fixed_amount';
  discount_value: number;
  currency: string;
  max_uses: number | null;
  used_count: number;
  usage_count: number;
  max_uses_per_customer: number;
  valid_from: string | null;
  valid_until: string | null;
  min_purchase_amount: number | null;
  applicable_plans: string[] | null;
  stripe_coupon_id: string | null;
  is_active: boolean;
  created_at: string;
  total_discount_given: number;
}

export async function getAdminPromoCodes(): Promise<{ promo_codes: PromoCode[] }> {
  const response = await adminApi.get('/api/admin/promo-codes');
  return response.data;
}

export async function createAdminPromoCode(data: Partial<PromoCode>): Promise<{ promo_code: PromoCode }> {
  const response = await adminApi.post('/api/admin/promo-codes', data);
  return response.data;
}

export async function updateAdminPromoCode(id: string, data: Partial<PromoCode>): Promise<{ promo_code: PromoCode }> {
  const response = await adminApi.put(`/api/admin/promo-codes/${id}`, data);
  return response.data;
}

export async function deleteAdminPromoCode(id: string): Promise<void> {
  await adminApi.delete(`/api/admin/promo-codes/${id}`);
}

// ─── Stripe config (DB) ──────────────────────────────────────────────────────

export interface AdminStripeConfig {
  id: string;
  publishable_key: string;
  mode: 'live' | 'test' | '';
  has_secret_key: boolean;
  has_webhook_secret: boolean;
  is_active: boolean;
  last_tested_at: string | null;
  test_status: 'ok' | 'error' | null;
  updated_at: string;
  price_ids: Record<string, { monthly: string; yearly: string }>;
}

export async function getAdminStripeConfig(): Promise<{ config: AdminStripeConfig | null }> {
  const response = await adminApi.get('/api/admin/stripe-config');
  return response.data;
}

export async function updateAdminStripeConfig(
  data: Partial<AdminStripeConfig> & { secret_key?: string; webhook_secret?: string; price_ids?: Record<string, { monthly: string; yearly: string }> }
): Promise<{ config: AdminStripeConfig }> {
  const response = await adminApi.put('/api/admin/stripe-config', data);
  return response.data;
}

export async function testAdminStripeConfig(): Promise<{ success: boolean; message: string }> {
  const response = await adminApi.post('/api/admin/stripe-config/test');
  return response.data;
}

// ─── Azure Storage config (DB) ───────────────────────────────────────────────

export interface AdminAzureStorageConfig {
  id: string;
  account_name: string;
  architectures_container: string;
  bundles_container: string;
  tfstate_resource_group: string;
  tfstate_container: string;
  has_account_key: boolean;
  is_active: boolean;
  last_tested_at: string | null;
  test_status: 'ok' | 'error' | null;
  updated_at: string;
}

export async function getAdminAzureStorageConfig(): Promise<{ config: AdminAzureStorageConfig | null }> {
  const response = await adminApi.get('/api/admin/azure-storage-config');
  return response.data;
}

export async function updateAdminAzureStorageConfig(
  data: Partial<AdminAzureStorageConfig> & { account_key?: string }
): Promise<{ config: AdminAzureStorageConfig }> {
  const response = await adminApi.put('/api/admin/azure-storage-config', data);
  return response.data;
}

export async function testAdminAzureStorageConfig(): Promise<{ success: boolean; message: string }> {
  const response = await adminApi.post('/api/admin/azure-storage-config/test');
  return response.data;
}

// ─── Factory Projects ─────────────────────────────────────────────────────────

export interface FactoryProject {
  id: string;
  name: string;
  plan_code: string;
  ado_project: string;
  ado_organization: string | null;
  pipeline_id: number | null;      // Azure pipeline (backward compat)
  pipeline_id_aws: number | null;  // AWS pipeline
  pipeline_id_gcp: number | null;  // GCP pipeline
  max_concurrency: number;
  active_runs: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export async function getFactoryProjects(): Promise<{ factory_projects: FactoryProject[] }> {
  const response = await adminApi.get('/api/admin/factory-projects');
  return response.data;
}

export async function createFactoryProject(data: {
  name: string;
  plan_code: string;
  max_concurrency?: number;
}): Promise<{ factory_project: FactoryProject; bootstrap: { project: string | null; agentPool: string | null; pipeline: number | null } }> {
  const response = await adminApi.post('/api/admin/factory-projects', data);
  return response.data;
}

export interface BootstrapProgressEvent {
  step:    'start' | 'project' | 'agentPool' | 'queue' | 'variableGroup' | 'yaml' | 'pipeline' | 'pipelineVars' | 'agents' | 'done' | 'error';
  status:  'running' | 'success' | 'warning' | 'error';
  message: string;
  detail?: string;
  result?: Record<string, unknown>;
}

export interface BootstrapFPResult {
  success: boolean;
  factory_project_id: string;
  bootstrap: { project: string | null; agentPool: string | null; pipeline: number | null; yamlPushed?: boolean; yamlWarning?: string };
}

/**
 * Ouvre un flux SSE vers /bootstrap/stream et appelle onEvent pour chaque étape.
 * Retourne une fonction cleanup() pour fermer le flux.
 */
export function streamBootstrapFactoryProject(
  id: string,
  onEvent: (event: BootstrapProgressEvent) => void
): () => void {
  const token  = localStorage.getItem('admin_access_token') || '';
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  // EventSource ne supporte pas les headers — on passe le token en query param
  const url = `${apiUrl}/api/admin/factory-projects/${id}/bootstrap/stream?token=${encodeURIComponent(token)}`;
  const es   = new EventSource(url);

  es.onmessage = (e) => {
    try {
      const event: BootstrapProgressEvent = JSON.parse(e.data);
      onEvent(event);
      if (event.step === 'done' || event.step === 'error') es.close();
    } catch { /* ignore parse errors */ }
  };

  es.onerror = () => {
    onEvent({ step: 'error', status: 'error', message: 'Connexion SSE perdue' });
    es.close();
  };

  return () => es.close();
}

export async function bootstrapFactoryProject(id: string): Promise<BootstrapFPResult> {
  const response = await adminApi.post(`/api/admin/factory-projects/${id}/bootstrap`);
  return response.data;
}

export async function updateFactoryProject(
  id: string,
  data: Partial<Pick<FactoryProject, 'name' | 'ado_project' | 'pipeline_id' | 'pipeline_id_aws' | 'pipeline_id_gcp' | 'max_concurrency' | 'is_active'>>
): Promise<{ factory_project: FactoryProject }> {
  const response = await adminApi.patch(`/api/admin/factory-projects/${id}`, data);
  return response.data;
}

export async function deleteFactoryProject(id: string): Promise<void> {
  await adminApi.delete(`/api/admin/factory-projects/${id}`);
}

// ─── Enterprise Org Config ────────────────────────────────────────────────────

export interface EnterpriseOrgConfig {
  id: string;
  organization_id: string;
  storage_account_name: string | null;
  has_key: boolean;
  bundles_container: string;
  tfstate_container: string;
  tfstate_resource_group: string | null;
  is_provisioned: boolean;
  notes: string | null;
  updated_at: string;
}

export async function getEnterpriseOrgConfig(orgId: string): Promise<{ config: EnterpriseOrgConfig | null }> {
  const response = await adminApi.get(`/api/admin/enterprise-onboarding/${orgId}`);
  return response.data;
}

export async function upsertEnterpriseOrgConfig(
  orgId: string,
  data: Partial<EnterpriseOrgConfig> & { storage_account_key?: string }
): Promise<{ config: EnterpriseOrgConfig }> {
  const response = await adminApi.put(`/api/admin/enterprise-onboarding/${orgId}`, data);
  return response.data;
}

export async function testEnterpriseStorageConfig(
  orgId: string
): Promise<{ success: boolean; message: string }> {
  const response = await adminApi.post(`/api/admin/enterprise-onboarding/${orgId}/test`);
  return response.data;
}

// ─── Bootstrap Org ADO ────────────────────────────────────────────────────────

export interface BootstrapOrgResult {
  success: boolean;
  plan_code: string;
  result: {
    project:       string | null;
    agentPool:     string | null;
    pipeline:      number | null;
    variableGroup: string | null;
  };
}

export async function bootstrapAdoOrg(data: {
  plan_code:   string;
  ado_org_url: string;
  pat:         string;
  repo_url?:   string;
  signing_key?: string;
}): Promise<BootstrapOrgResult> {
  const response = await adminApi.post('/api/admin/bootstrap-org', data);
  return response.data;
}

// ─── Platform Config ──────────────────────────────────────────────────────────

export interface AwsRegion {
  code:  string;
  label: string;
  group: string;
}

export async function getPlatformConfig(key: string): Promise<{ key: string; value: unknown; updated_at: string }> {
  const response = await adminApi.get(`/api/admin/platform-config/${key}`);
  return response.data;
}

export async function updatePlatformConfig(key: string, value: unknown): Promise<{ success: boolean; key: string }> {
  const response = await adminApi.patch(`/api/admin/platform-config/${key}`, { value });
  return response.data;
}

export async function syncAwsRegions(): Promise<{ success: boolean; count: number; regions: AwsRegion[] }> {
  const response = await adminApi.post('/api/admin/platform-config/aws_regions/sync');
  return response.data;
}

// ─── Enterprise Execution Lanes ───────────────────────────────────────────────

export interface TenantExecutionLane {
  id:                  string;
  organization_id:     string;
  organization_name?:  string;
  organization_slug?:  string;
  label:               string;
  ado_organization:    string;
  ado_pool_name:       string;
  ado_pool_id:         number | null;
  ado_project:         string;
  max_concurrency:     number;
  min_agents_required: number;
  agents_online:       number;
  active_jobs:         number;
  current_jobs?:       number;  // live count from DB
  is_active:           boolean;
  k8s_namespace:       string | null;
  k8s_manifest:        string | null;
  notes:               string | null;
  created_at:          string;
  updated_at:          string;
}

export interface LaneCreatePayload {
  organization_id:      string;
  label:                string;
  ado_organization:     string;
  ado_pool_name:        string;
  ado_project?:         string;
  max_concurrency?:     number;
  min_agents_required?: number;
  k8s_namespace?:       string;
  notes?:               string;
}

export async function getAdminLanes(organization_id?: string): Promise<TenantExecutionLane[]> {
  const params = organization_id ? { organization_id } : {};
  const response = await adminApi.get('/api/admin/lanes', { params });
  return response.data.lanes;
}

export async function getAdminLane(id: string): Promise<TenantExecutionLane> {
  const response = await adminApi.get(`/api/admin/lanes/${id}`);
  return response.data.lane;
}

export async function createAdminLane(data: LaneCreatePayload): Promise<TenantExecutionLane> {
  const response = await adminApi.post('/api/admin/lanes', data);
  return response.data.lane;
}

export async function updateAdminLane(id: string, data: Partial<LaneCreatePayload & { is_active: boolean }>): Promise<TenantExecutionLane> {
  const response = await adminApi.patch(`/api/admin/lanes/${id}`, data);
  return response.data.lane;
}

export async function deleteAdminLane(id: string): Promise<void> {
  await adminApi.delete(`/api/admin/lanes/${id}`);
}

export async function provisionAdminLanePool(id: string): Promise<{ success: boolean; poolId: number; poolName: string }> {
  const response = await adminApi.post(`/api/admin/lanes/${id}/provision`);
  return response.data;
}

export async function getAdminLaneManifest(id: string, regenerate = false): Promise<{ manifest: string; namespace: string }> {
  const response = await adminApi.get(`/api/admin/lanes/${id}/manifest`, {
    params: regenerate ? { regenerate: '1' } : {},
  });
  return response.data;
}

export async function refreshAdminLaneHealth(id: string): Promise<{ success: boolean; agentsOnline: number; agentsTotal: number }> {
  const response = await adminApi.post(`/api/admin/lanes/${id}/health`);
  return response.data;
}

export async function refreshAllLanesHealth(): Promise<{ results: Array<{ laneId: string; status: string; value: unknown; error: string | null }> }> {
  const response = await adminApi.post('/api/admin/lanes/health/all');
  return response.data;
}

export async function backfillEnterpriseLanes(): Promise<{
  results: Array<{ organization_id: string; status: string; error?: string }>;
  total: number;
}> {
  const response = await adminApi.post('/api/admin/lanes/backfill');
  return response.data;
}

export interface LaneProvisionEvent {
  step:    'init' | 'ado_pool' | 'db_status' | 'k8s_manifest' | 'k8s_apply' | 'agents_poll' | 'done' | 'error';
  status:  'running' | 'success' | 'warning' | 'error';
  message: string;
  data?:   Record<string, unknown>;
}

/**
 * Ouvre un flux SSE vers /activate/stream et appelle onEvent pour chaque étape.
 * Retourne une fonction cleanup() pour fermer le flux.
 */
export function streamActivateLane(
  id: string,
  onEvent: (event: LaneProvisionEvent) => void
): () => void {
  const token  = localStorage.getItem('admin_access_token') || '';
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const url    = `${apiUrl}/api/admin/lanes/${id}/activate/stream?token=${encodeURIComponent(token)}`;
  const es     = new EventSource(url);

  es.onmessage = (e) => {
    try {
      const event: LaneProvisionEvent = JSON.parse(e.data);
      onEvent(event);
      if (event.step === 'done' || event.step === 'error') es.close();
    } catch { /* ignore parse errors */ }
  };

  es.onerror = () => {
    onEvent({ step: 'error', status: 'error', message: 'Connexion SSE perdue' });
    es.close();
  };

  return () => es.close();
}

export interface BusinessLaneProvisionEvent {
  step:    'init' | 'byoo_creds' | 'k8s_manifest' | 'k8s_apply' | 'agents_poll' | 'done' | 'error' | string;
  status:  'running' | 'success' | 'warning' | 'error';
  message: string;
  data?:   Record<string, unknown>;
}

/**
 * Ouvre un flux SSE vers /business/lanes/:laneId/activate/stream.
 * Retourne une fonction cleanup() pour fermer le flux.
 */
export function streamActivateBusinessLane(
  laneId:  string,
  onEvent: (event: BusinessLaneProvisionEvent) => void
): () => void {
  const token  = localStorage.getItem('admin_access_token') || '';
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const url    = `${apiUrl}/api/admin/business/lanes/${laneId}/activate/stream?token=${encodeURIComponent(token)}`;
  const es     = new EventSource(url);

  es.onmessage = (e) => {
    try {
      const event: BusinessLaneProvisionEvent = JSON.parse(e.data);
      onEvent(event);
      if (event.step === 'done' || event.step === 'error') es.close();
    } catch { /* ignore parse errors */ }
  };

  es.onerror = () => {
    onEvent({ step: 'error', status: 'error', message: 'Connexion SSE perdue' });
    es.close();
  };

  return () => es.close();
}

// ─── Bootstrap Enterprise/Business Pipelines (SSE) ────────────────────────────

export interface BootstrapEnterprisePipelineEvent {
  step:    'start' | 'project' | 'yaml' | 'pipeline' | 'pipelineVars' | 'pipelinePermission' | 'done' | 'error' | string;
  status:  'running' | 'success' | 'warning' | 'error';
  message: string;
  detail?: string;
  result?: { project?: string | null; pipelineAzure?: number | null; pipelineAws?: number | null; pipelineGcp?: number | null; pipelineFabric?: number | null };
}

export interface BusinessOrgByoo {
  organization_id:  string;
  organization_name: string;
  organization_slug: string;
  byoo_id:          string | null;
  azure_org_name:   string | null;
  azure_org_url:    string | null;
  is_validated:     boolean;
  bootstrap_status: string | null;
  pipeline_id_azure:  number | null;
  pipeline_id_aws:    number | null;
  pipeline_id_gcp:    number | null;
  pipeline_id_fabric: number | null;
  ado_project_name:   string | null;
  lane_id:            string | null;
  lane_active:        boolean | null;
  lane_provision_status: string | null;
  lane_agents_online: number | null;
}

export const getBusinessOrgs = (): Promise<{ orgs: BusinessOrgByoo[] }> =>
  adminApi.get('/api/admin/business/orgs').then(r => r.data);

export function streamBootstrapBusinessOrg(
  organizationId: string,
  onEvent: (event: BootstrapEnterprisePipelineEvent) => void
): () => void {
  const token  = localStorage.getItem('admin_access_token') || '';
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const url    = `${apiUrl}/api/admin/business/orgs/${organizationId}/bootstrap/stream?token=${encodeURIComponent(token)}`;
  const es     = new EventSource(url);

  es.onmessage = (e) => {
    try {
      const event: BootstrapEnterprisePipelineEvent = JSON.parse(e.data);
      onEvent(event);
      if (event.step === 'done' || event.step === 'error') es.close();
    } catch { /* ignore */ }
  };

  es.onerror = () => {
    onEvent({ step: 'error', status: 'error', message: 'Connexion SSE perdue' });
    es.close();
  };

  return () => es.close();
}

/**
 * Ouvre un flux SSE vers /bootstrap-enterprise/stream et appelle onEvent pour chaque étape.
 * Retourne une fonction cleanup() pour fermer le flux.
 */
export function streamBootstrapEnterprisePipeline(
  planCode: 'ENTREPRISE' | 'BUSINESS',
  onEvent: (event: BootstrapEnterprisePipelineEvent) => void
): () => void {
  const token  = localStorage.getItem('admin_access_token') || '';
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const url    = `${apiUrl}/api/admin/bootstrap-enterprise/stream?plan_code=${planCode}&token=${encodeURIComponent(token)}`;
  const es     = new EventSource(url);

  es.onmessage = (e) => {
    try {
      const event: BootstrapEnterprisePipelineEvent = JSON.parse(e.data);
      onEvent(event);
      if (event.step === 'done' || event.step === 'error') es.close();
    } catch { /* ignore parse errors */ }
  };

  es.onerror = () => {
    onEvent({ step: 'error', status: 'error', message: 'Connexion SSE perdue' });
    es.close();
  };

  return () => es.close();
}

// ─── Configuration LLM / IA ───────────────────────────────────────────────────

export interface AiConfigRow {
  key: string;
  value: string;
  value_type: 'string' | 'boolean' | 'integer' | 'json' | 'secret';
  description: string;
  updated_at: string;
}

export interface AiUsageStats {
  totalRequests: number;
  totalTokens: number;
  totalCostUsd: number;
  breakdown: { provider: string; task_type: string; total_requests: number; total_tokens: number; total_cost_usd: string }[];
}

export const getAiConfig = (): Promise<{ configs: AiConfigRow[] }> =>
  adminApi.get('/api/admin/ai-config').then(r => r.data);

export const updateAiConfigKey = (key: string, value: string | boolean | number): Promise<{ success: boolean }> =>
  adminApi.put(`/api/admin/ai-config/${key}`, { value }).then(r => r.data);

export const testAiConnection = (provider: 'claude' | 'openai' = 'claude'): Promise<{ success: boolean; model?: string; latencyMs?: number; error?: string }> =>
  adminApi.post('/api/admin/ai-config/test-connection', { provider }, { timeout: 30000 }).then(r => r.data);

export const getAiUsage = (from?: string, to?: string): Promise<AiUsageStats> =>
  adminApi.get('/api/admin/ai-config/usage', { params: { from, to } }).then(r => r.data);
